import { useState, useEffect } from 'react';
import { resumeParseService, ResumeParseConfig } from '../../../services/resumeParseService';

export default function ResumeParseConfigPanel() {
  const [config, setConfig] = useState<ResumeParseConfig | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    enabled: true,
    function_url: 'https://hlurukdritwcnlflmvam.supabase.co/functions/v1/parse-resume',
    mode: 'standard' as 'fast' | 'standard' | 'ultimate',
    parse_avatar: 0 as 0 | 1,
    timeout_ms: 20000,
    retry: 1,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const data = await resumeParseService.getActiveConfig();
    setConfig(data);
    if (data) {
      setFormData({
        enabled: data.enabled,
        function_url: data.function_url,
        mode: data.mode || 'standard',
        parse_avatar: data.parse_avatar ?? 0,
        timeout_ms: data.timeout_ms ?? 20000,
        retry: data.retry ?? 1,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await resumeParseService.setActiveConfig(formData);
      alert('配置保存成功！');
      setShowForm(false);
      loadConfig();
    } catch (error) {
      console.error('保存配置失败:', error);
      alert('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    if (config) {
      setFormData({
        enabled: config.enabled,
        function_url: config.function_url,
        mode: config.mode || 'standard',
        parse_avatar: config.parse_avatar ?? 0,
        timeout_ms: config.timeout_ms ?? 20000,
        retry: config.retry ?? 1,
      });
    }
  };

  const handleEdit = () => {
    setShowForm(true);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">简历解析配置</h2>
          <p className="text-sm text-gray-500 mt-1">配置 Supabase Edge Function 简历解析接口</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-settings-3-line mr-2"></i>
            {config ? '编辑配置' : '添加配置'}
          </button>
        )}
      </div>

      {showForm ? (
        <form onSubmit={handleSubmit} className="space-y-6 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
            <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
              启用简历解析功能
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Edge Function URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.function_url}
              onChange={(e) => setFormData({ ...formData, function_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="https://your-project.supabase.co/functions/v1/parse-resume"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Supabase Edge Function 的完整地址</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              解析模式 <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.mode}
              onChange={(e) => setFormData({ ...formData, mode: e.target.value as 'fast' | 'standard' | 'ultimate' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="fast">Fast（快速模式，6MB 以内）</option>
              <option value="standard">Standard（标准模式，15MB 以内）</option>
              <option value="ultimate">Ultimate（终极模式，20MB 以内）</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">不同模式支持的文件大小和解析精度不同</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">解析头像</label>
            <select
              value={formData.parse_avatar}
              onChange={(e) => setFormData({ ...formData, parse_avatar: Number(e.target.value) as 0 | 1 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value={0}>不解析</option>
              <option value={1}>解析</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">是否从简历中提取头像信息</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">超时时间（毫秒）</label>
            <input
              type="number"
              value={formData.timeout_ms}
              onChange={(e) => setFormData({ ...formData, timeout_ms: Number(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              min="5000"
              max="60000"
              step="1000"
            />
            <p className="text-xs text-gray-500 mt-1">请求超时时间，建议 20000-30000 毫秒</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">重试次数</label>
            <input
              type="number"
              value={formData.retry}
              onChange={(e) => setFormData({ ...formData, retry: Number(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              min="0"
              max="3"
            />
            <p className="text-xs text-gray-500 mt-1">网络失败时的重试次数，建议 1-2 次</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">配置说明：</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>请确保已在 Supabase 控制台部署 parse-resume Edge Function</li>
                  <li>需要在 Edge Function 中设置环境变量：QINGSONG_USER_ID 和 QINGSONG_USER_SECRET</li>
                  <li>Edge Function 会自动处理文件编码和接口调用</li>
                  <li>配置保存在浏览器本地存储中</li>
                  <li>保存后可以在添加人才时测试解析功能</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {loading ? '保存中...' : '保存配置'}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          {!config ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-settings-3-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-gray-500">暂无配置，请添加简历解析接口配置</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-4 hover:border-teal-300 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="font-medium text-gray-900">简历解析配置</h3>
                    {config.enabled ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full whitespace-nowrap">
                        已启用
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full whitespace-nowrap">
                        已禁用
                      </span>
                    )}
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>
                      <span className="font-medium">Edge Function:</span>{' '}
                      <span className="text-xs break-all">{config.function_url}</span>
                    </p>
                    <p>
                      <span className="font-medium">解析模式:</span>{' '}
                      {config.mode === 'fast' && 'Fast（快速）'}
                      {config.mode === 'standard' && 'Standard（标准）'}
                      {config.mode === 'ultimate' && 'Ultimate（终极）'}
                    </p>
                    <p>
                      <span className="font-medium">解析头像:</span> {config.parse_avatar ? '是' : '否'}
                    </p>
                    <p>
                      <span className="font-medium">超时时间:</span> {config.timeout_ms}ms
                    </p>
                    <p>
                      <span className="font-medium">重试次数:</span> {config.retry}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleEdit}
                  className="ml-4 px-3 py-1 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className="ri-edit-line mr-1"></i>
                  编辑
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
